import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { api, ApiError } from '../../lib/api';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { createEntitySchema, type CreateEntityFormData } from '../../schemas/entity';

export default function EntitiesPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-entities'],
    queryFn: () => api.admin.entities.list(),
  });

  // Get unique countries for filter dropdown
  const countries = useMemo(() => {
    if (!data?.data) return [];
    const uniqueCountries = [...new Set(data.data.map(e => e.country).filter(Boolean))] as string[];
    return uniqueCountries.sort();
  }, [data?.data]);

  // Filter entities
  const filteredEntities = useMemo(() => {
    if (!data?.data) return [];

    let entities = [...data.data];

    // Search filter
    if (search) {
      const s = search.toLowerCase();
      entities = entities.filter(e =>
        e.code.toLowerCase().includes(s) ||
        e.name.toLowerCase().includes(s)
      );
    }

    // Country filter
    if (countryFilter !== 'all') {
      entities = entities.filter(e => e.country === countryFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      entities = entities.filter(e => e.isActive === isActive);
    }

    return entities;
  }, [data?.data, search, countryFilter, statusFilter]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('entities.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          {t('entities.newEntity')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          className="form-input !w-auto min-w-[220px]"
          placeholder="Search code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-input !w-auto min-w-[150px]"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
        >
          <option value="all">{tc('all')} Countries</option>
          {countries.map(country => (
            <option key={country} value={country}>{country}</option>
          ))}
        </select>
        <select
          className="form-input !w-auto min-w-[150px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{tc('all')} Status</option>
          <option value="active">{tc('status.active')}</option>
          <option value="inactive">{tc('status.inactive')}</option>
        </select>
        <span className="ml-auto text-sm text-foreground-muted">{filteredEntities.length} entities</span>
      </div>

      {isLoading ? (
        <p className="text-muted">{tc('actions.loading')}</p>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>{t('entities.fields.code')}</th>
                <th>{t('entities.fields.name')}</th>
                <th>{t('entities.fields.country')}</th>
                <th>{t('entities.fields.defaultCurrency')}</th>
                <th>{tc('status.label')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntities.map((entity) => (
                <tr key={entity.id}>
                  <td><strong>{entity.code}</strong></td>
                  <td>{entity.name}</td>
                  <td>{entity.country || '-'}</td>
                  <td>{entity.defaultCurrency || '-'}</td>
                  <td>
                    <span className={`badge ${entity.isActive ? 'badge-closed' : 'badge-open'}`}>
                      {entity.isActive ? tc('status.active') : tc('status.inactive')}
                    </span>
                  </td>
                </tr>
              ))}
              {filteredEntities.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    {data?.data.length === 0 ? t('entities.noEntities') : 'No matching entities'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <EntityFormModal
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-entities'] });
            setShowForm(false);
          }}
        />
      )}

    </div>
  );
}

function EntityFormModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateEntityFormData>({
    resolver: zodResolver(createEntitySchema),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateEntityFormData) => api.admin.entities.create({
      code: data.code.toUpperCase(),
      name: data.name,
      country: data.country || undefined,
      taxId: data.taxId || undefined,
      defaultCurrency: data.defaultCurrency?.toUpperCase() || undefined,
    }),
    onSuccess: () => {
      toast.success('Entity created successfully');
      onSuccess();
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  const onSubmit = (data: CreateEntityFormData) => {
    createMutation.mutate(data);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('entities.newEntity')}
      size="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">{t('entities.fields.code')}</label>
          <input
            type="text"
            className={`form-input uppercase ${errors.code ? 'border-error' : ''}`}
            placeholder="FNK"
            {...register('code')}
          />
          {errors.code ? (
            <span className="block text-error text-[13px] mt-1">{errors.code.message}</span>
          ) : (
            <small className="text-muted">{t('entities.codeHint')}</small>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('entities.fields.name')}</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-error' : ''}`}
            placeholder="F&K Master S.L."
            {...register('name')}
          />
          {errors.name && (
            <span className="block text-error text-[13px] mt-1">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('entities.fields.country')}</label>
          <input
            type="text"
            className="form-input"
            placeholder="Spain"
            {...register('country')}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('entities.fields.taxId')}</label>
          <input
            type="text"
            className="form-input"
            placeholder="Optional"
            {...register('taxId')}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('entities.fields.defaultCurrency')}</label>
          <input
            type="text"
            className={`form-input uppercase ${errors.defaultCurrency ? 'border-error' : ''}`}
            maxLength={3}
            placeholder="EUR"
            {...register('defaultCurrency')}
          />
          {errors.defaultCurrency && (
            <span className="block text-error text-[13px] mt-1">{errors.defaultCurrency.message}</span>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? tc('actions.loading') : tc('actions.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
